import React, { useState, useEffect, useCallback } from 'react';
import './AdminPanel.css';

interface AllowedUser {
  garminEmail: string;
  addedAt?: string;
  userId?: string;
  displayName?: string;
  isAdmin: boolean;
  connections: {
    garmin: boolean;
    strava: boolean;
    dropbox: boolean;
    barrys: boolean;
  };
}

export const AdminPanel: React.FC = () => {
  const [users, setUsers] = useState<AllowedUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState('');

  const getHeaders = useCallback(() => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = localStorage.getItem('authToken');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const response = await fetch('/api/admin/users', { headers: getHeaders() });
      if (!response.ok) throw new Error('Failed to load users');
      const data = await response.json();
      setUsers(data.users);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const addUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail.trim()) return;

    setIsAdding(true);
    setError('');

    try {
      const response = await fetch('/api/admin/users', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ garminEmail: newEmail.trim() }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to add user');
      }

      setNewEmail('');
      await loadUsers();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsAdding(false);
    }
  };

  const removeUser = async (email: string) => {
    if (!window.confirm(`Remove ${email} from the allowlist?`)) return;

    try {
      const response = await fetch(`/api/admin/users/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to remove user');
      }

      await loadUsers();
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (isLoading) {
    return (
      <div className="admin-panel">
        <p>Loading users...</p>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <h3 className="admin-panel__title">Allowed Users</h3>
      <p className="admin-panel__description">Manage which Garmin accounts can sign in to FitSync.</p>

      {error && <div className="admin-panel__error">{error}</div>}

      <form onSubmit={addUser} className="admin-panel__form">
        <input
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          placeholder="garmin@email.com"
          className="admin-panel__input"
          disabled={isAdding}
        />
        <button type="submit" className="admin-panel__add-button" disabled={isAdding || !newEmail.trim()}>
          {isAdding ? 'Adding...' : 'Add User'}
        </button>
      </form>

      <div className="admin-panel__list">
        {users.map((user) => (
          <div key={user.garminEmail} className="admin-panel__user">
            <div className="admin-panel__user-info">
              <span className="admin-panel__user-email">
                {user.garminEmail}
                {user.isAdmin && <span className="admin-panel__badge">Admin</span>}
              </span>
              <div className="admin-panel__user-connections">
                <span
                  className={`admin-panel__dot ${user.connections.garmin ? 'admin-panel__dot--active' : ''}`}
                  title="Garmin"
                >
                  G
                </span>
                <span
                  className={`admin-panel__dot ${user.connections.strava ? 'admin-panel__dot--active' : ''}`}
                  title="Strava"
                >
                  S
                </span>
                <span
                  className={`admin-panel__dot ${user.connections.dropbox ? 'admin-panel__dot--active' : ''}`}
                  title="Dropbox"
                >
                  D
                </span>
                <span
                  className={`admin-panel__dot ${user.connections.barrys ? 'admin-panel__dot--active' : ''}`}
                  title="Barry's"
                >
                  B
                </span>
              </div>
            </div>
            {!user.isAdmin && (
              <button onClick={() => removeUser(user.garminEmail)} className="admin-panel__remove-button">
                Remove
              </button>
            )}
          </div>
        ))}
        {users.length === 0 && <p className="admin-panel__empty">No users yet. Add a Garmin email above.</p>}
      </div>
    </div>
  );
};
